import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * Shared destructive-confirmation dialog (notes, tasks, custom fields, logout).
 * Styled to match the app's panel language — ink gradient, hairline border,
 * icon tile, footer actions — with a red accent instead of the usual brand blue
 * so a destructive step never reads like a routine one.
 *
 * Keeps `z-[200]`: it stacks on top of other dialogs (e.g. TaskOverviewModal).
 */

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    description?: string;
    confirmButtonText?: string;
    isDeleting?: boolean;
    /** Optional extra content below the description (e.g. a reassign selector). */
    children?: React.ReactNode;
};

export function DeleteConfirmationModal({
    open,
    onClose,
    onConfirm,
    title = "Are you sure you want to delete this item?",
    description,
    confirmButtonText = "Yes, Delete",
    isDeleting = false,
    children
}: Props) {
    return (
        <Dialog open={open} onOpenChange={isDeleting ? undefined : onClose}>
            <DialogContent className="z-[200] w-full max-w-[90vw] gap-0 overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-ink-800/95 to-ink-900/95 p-0 shadow-[0_32px_90px_-16px_rgba(0,0,0,0.75)] backdrop-blur-xl sm:max-w-md">
                <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-red-500/20 blur-3xl" />

                <DialogHeader className="relative px-6 pb-5 pt-6">
                    <div className="flex items-start gap-3.5">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-red-400/25 to-red-700/10 ring-1 ring-red-500/30">
                            <AlertTriangle className="h-[18px] w-[18px] shrink-0 text-red-300" />
                        </div>
                        <div className="min-w-0 pt-0.5 text-left">
                            <DialogTitle className="text-[15.5px] font-semibold leading-snug tracking-tight text-white">
                                {title}
                            </DialogTitle>
                            {description && (
                                <p className="mt-1 text-[11.5px] leading-relaxed text-slate-400">{description}</p>
                            )}
                            {children && <div className="mt-3">{children}</div>}
                        </div>
                    </div>
                </DialogHeader>

                <div className="relative flex justify-end gap-2 border-t border-white/[0.06] bg-white/[0.015] px-6 py-4">
                    <button
                        type="button"
                        onClick={isDeleting ? undefined : onClose}
                        disabled={isDeleting}
                        className="inline-flex h-9 items-center rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 text-[12.5px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={isDeleting ? undefined : onConfirm}
                        disabled={isDeleting}
                        className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-red-500/15 px-4 text-[12.5px] font-semibold text-red-200 ring-1 ring-red-500/30 transition hover:bg-red-500/25 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isDeleting && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />}
                        {confirmButtonText}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
